import {
  AgenticLogicContextDescriptions,
  AgenticLogicIngressRequestDescriptions,
  AgenticLogicPriceFilterDescriptions,
  AgenticLogicProductQueryDescriptions,
  AgenticLogicProductScanDescriptions,
  AgenticLogicRequestDescriptions,
  AgenticLogicResultItemDescriptions,
  AgenticLogicScanTelemetryDescriptions,
  AgenticLogicShippingContextDescriptions,
} from "../../descriptions/agentic-logic.describe";
import type { AgenticLogicProductPostFetchWhere } from "../../functions/agentic-logic/sdk/post-fetch-where";
import {
  AGENTIC_LOGIC_PRODUCT_QUERY_MAX_LIMIT,
  AGENTIC_LOGIC_PRODUCT_SCAN_MAX_PAGE_SIZE,
  AGENTIC_LOGIC_PRODUCT_SCAN_MAX_PRODUCTS,
  AGENTIC_LOGIC_QUERY_LIMIT_HEADROOM_FACTOR,
  AGENTIC_LOGIC_QUERY_LIMIT_HEADROOM_FLOOR,
  AGENTIC_LOGIC_STOREFRONT_INPUT_LIST_MAX,
  type AgenticLogicProductQuery,
} from "../../functions/agentic-logic/sdk/product-query";
import { AGENTIC_LOGIC_MAX_SDK_READS_PER_INVOCATION } from "../../functions/agentic-logic/runtime/sdk-read";
import {
  agenticLogicRenderableKinds,
  agenticLogicSupportedIntents,
  agenticLogicUnrenderableKinds,
  agenticLogicUnsupportedIntents,
} from "../../functions/agentic-logic/gates/renderable-kinds";
import {
  AgenticLogicItemIntentList,
  AgenticLogicItemKindList,
} from "../../functions/agentic-logic/types/vocab";
import type { AgenticLogicResultItem } from "../../functions/agentic-logic/types/result";
import type { AgenticLogicProductServerWhere } from "../../functions/agentic-logic/sdk/server-where";
import type { AgenticLogicProductPage } from "../../functions/agentic-logic/sdk/product-page";
import type { AgenticLogicContext } from "../../functions/agentic-logic/types/invocation";
import type { AgenticLogicShippingContext } from "../../functions/agentic-logic/types/shipping";
import { type AgenticLogicSurface, AgenticLogicSurfaceList } from "./surface";
import {
  AGENTIC_LOGIC_SURFACE_FIELD_AVAILABILITY,
  AGENTIC_LOGIC_SURFACE_FIELD_CAVEATS,
  AGENTIC_LOGIC_SURFACE_LINE_FLAG_FIDELITY,
  type AgenticLogicCartLineFlag,
  AgenticLogicCartLineFlagList,
  agenticLogicLineBlock,
  type AgenticLogicSurfaceInputField,
  AgenticLogicSurfaceInputFieldList,
} from "./surface-inputs";

// ---------------------------------------------------------------------------
// ONE rendering of the rule-authoring request contract.
//
// The same facts used to be hand-written in four places — the TypeScript the
// model reads as `sdkTypes`, the workspace's runtime-contract blurb, and two
// prompt files — none of which could see the others. This module renders them
// from the descriptions that are already canonical plus the per-surface
// availability table, so the copies become one call.
//
// Rendering is deliberately plain text: it has to read well inside a TS doc
// comment, inside a markdown prompt, and inside a UI panel, and none of those
// can carry structure the others would show as noise.
// ---------------------------------------------------------------------------

/** Human phrasing for one field's availability across a set of surfaces. */
function availabilityNote(
  field: AgenticLogicSurfaceInputField,
  surfaces: readonly AgenticLogicSurface[]
): string {
  const always: AgenticLogicSurface[] = [];
  const sometimes: AgenticLogicSurface[] = [];
  const never: AgenticLogicSurface[] = [];
  for (const surface of surfaces) {
    const bucket = { always, sometimes, never }[
      AGENTIC_LOGIC_SURFACE_FIELD_AVAILABILITY[surface][field]
    ];
    bucket.push(surface);
  }

  const availability =
    never.length === surfaces.length
      ? `NEVER sent on ${list(never)} — do not build on it.`
      : (() => {
          const parts: string[] = [];
          if (always.length > 0) parts.push(`always sent on ${list(always)}`);
          if (sometimes.length > 0) {
            parts.push(`sometimes null on ${list(sometimes)} — null-check it`);
          }
          if (never.length > 0) parts.push(`never sent on ${list(never)}`);
          return `${parts.join("; ")}.`;
        })();

  return [availability, ...caveatClauses(field, surfaces)].join(" ");
}

/**
 * The phrase every generated line-flag caveat carries, and the actual fix for
 * X3.
 *
 * Disclosing that the flag is null was NOT enough on its own: told only what
 * is null, an authoring model still affirmed "Gift cards cannot seed
 * suggestions" back to the merchant. The model has to be told what to SAY, not
 * only what to read — so the imperative is mandatory in every generated
 * caveat, and the completeness test asserts on this exact constant.
 */
export const AGENTIC_LOGIC_LINE_FLAG_DISCLOSURE_IMPERATIVE =
  "DO NOT accept or restate";

/**
 * Per-flag prose for the generated caveats.
 *
 * Deliberately keyed by FLAG and not by (surface, flag): the fidelity table is
 * the per-surface truth, and duplicating its keys here would re-create the
 * second place this defect class lives in. A newly-declared dead pair renders
 * correct text with no prose edit.
 */
const LINE_FLAG_PROSE = {
  giftCard: {
    noun: "gift-card",
    whyNull:
      "no gift-card marker exists on that surface's line payload, so the producer sends none and the server refuses to fabricate a `false`",
    whyNeverTrue:
      "the producer answers for every line, but no line it reports can be a gift card",
    alternative:
      "A merchant-maintained tag or `productType` read through `sdk.products({ ids })` is an approximation, not the marker.",
  },
  syntheticGift: {
    noun: "gift-with-purchase",
    whyNull:
      "no `_igGWP` attribute reaches the server from that surface, so the producer sends none and the server refuses to fabricate a `false`",
    whyNeverTrue:
      "the producer answers for every line, but no line it reports can be an injected gift",
    alternative: undefined,
  },
  bundleChild: {
    noun: "bundle-component",
    whyNull:
      "no bundle marker exists on that surface's line payload, so the producer sends none and the server refuses to fabricate a `false`",
    whyNeverTrue:
      "`/cart.js` lists no bundle component lines at all, so every line it reports is a top-level one and there is nothing to exclude",
    alternative: undefined,
  },
  igUpsell: {
    noun: "Intelligems-upsell",
    whyNull:
      "no `_igUpsell`/`_igOnsiteUpsell` attribute reaches the server from that surface, so the producer sends none and the server refuses to fabricate a `false`",
    whyNeverTrue:
      "the producer answers for every line, but no line it reports can be an accepted upsell",
    alternative: undefined,
  },
} as const satisfies Record<
  AgenticLogicCartLineFlag,
  {
    readonly noun: string;
    readonly whyNull: string;
    readonly whyNeverTrue: string;
    readonly alternative: string | undefined;
  }
>;

/**
 * The GENERATED half of the caveat set: what a surface's line flags cannot
 * answer, rendered from {@link AGENTIC_LOGIC_SURFACE_LINE_FLAG_FIDELITY}.
 *
 * One clause per surface covering ALL of its dead flags, so post-purchase's
 * two collapse into one sentence rather than two near-identical ones the model
 * has to reconcile. `undefined` when every flag on the surface is `observed`.
 */
function lineFlagCaveat(surface: AgenticLogicSurface): string | undefined {
  const block = agenticLogicLineBlock(surface);

  const sentences = AgenticLogicCartLineFlagList.flatMap((flag) => {
    const fidelity = AGENTIC_LOGIC_SURFACE_LINE_FLAG_FIDELITY[surface][flag];
    if (fidelity === "observed") return [];

    const { noun, whyNull, whyNeverTrue, alternative } = LINE_FLAG_PROSE[flag];
    const dead =
      fidelity === "always-null"
        ? `is NULL on every ${surface} line — ${whyNull}`
        : `is never \`true\` on ${surface} — ${whyNeverTrue}`;

    return [
      `\`${block}.lines[].flags.${flag}\` ${dead}. A clause keyed on it ` +
        "(`=== true` or `!== true`) can never change the outcome, so " +
        `${AGENTIC_LOGIC_LINE_FLAG_DISCLOSURE_IMPERATIVE} a ${noun} exclusion ` +
        `as delivered on this surface — say plainly that ${surface} carries ` +
        `no ${noun} signal and the exclusion cannot be honoured there.` +
        (alternative === undefined ? "" : ` ${alternative}`),
    ];
  });

  return sentences.length === 0 ? undefined : sentences.join(" ");
}

/**
 * The sub-field caveats for one field, one clause per caveat, prefixed with
 * the surfaces it applies to.
 *
 * TWO sources, 0..n clauses: the hand-written
 * {@link AGENTIC_LOGIC_SURFACE_FIELD_CAVEATS} entry, and the line-flag caveat
 * generated for whichever of `cart`/`order` carries that surface's lines. They
 * coexist — at checkout the customer block and the cart block each carry one —
 * and the grouping below is unchanged, so `checkout.customer` renders
 * byte-identically to before.
 *
 * The `On <surface>:` prefix is load-bearing rather than decoration: the
 * all-surface rendering (`renderAgenticLogicRequestContract()` with no
 * argument, which is what the authoring context passes) puts this clause on a
 * line that also covers surfaces the caveat is not true of.
 */
function caveatClauses(
  field: AgenticLogicSurfaceInputField,
  surfaces: readonly AgenticLogicSurface[]
): string[] {
  const table: Partial<
    Record<
      AgenticLogicSurface,
      Partial<Record<AgenticLogicSurfaceInputField, string>>
    >
  > = AGENTIC_LOGIC_SURFACE_FIELD_CAVEATS;

  // Surfaces sharing one caveat collapse into a single clause; a caveat
  // repeated verbatim per surface is noise the model has to reconcile.
  const grouped = new Map<string, AgenticLogicSurface[]>();
  const add = (caveat: string, surface: AgenticLogicSurface): void => {
    const existing = grouped.get(caveat);
    if (existing) existing.push(surface);
    else grouped.set(caveat, [surface]);
  };

  for (const surface of surfaces) {
    const handWritten = table[surface]?.[field];
    if (handWritten !== undefined) add(handWritten, surface);

    if (field !== agenticLogicLineBlock(surface)) continue;
    const generated = lineFlagCaveat(surface);
    if (generated !== undefined) add(generated, surface);
  }

  return [...grouped].map(
    ([caveat, applies]) => `On ${list(applies)}: ${caveat}`
  );
}

function list(surfaces: readonly AgenticLogicSurface[]): string {
  if (surfaces.length === 1) return surfaces[0] as string;
  return `${surfaces.slice(0, -1).join(", ")} and ${surfaces[surfaces.length - 1]}`;
}

/**
 * The renderability disclosure, derived from
 * {@link AGENTIC_LOGIC_SURFACE_RENDERABLE_KINDS} so it can never claim a
 * capability the publish gate refuses.
 */
function renderRenderableKindsLine(
  surfaces: readonly AgenticLogicSurface[]
): string {
  const targeted = surfaces.length > 0 ? surfaces : AgenticLogicSurfaceList;
  const renderable = agenticLogicRenderableKinds(targeted);
  const undrawable = agenticLogicUnrenderableKinds(
    AgenticLogicItemKindList,
    targeted
  );

  return (
    `Items you return must be RENDERABLE on the surfaces this rule targets: ` +
    `${list(targeted)} ${targeted.length === 1 ? "draws" : "draw"} only ` +
    `${renderable.map((kind) => `\`${kind}\``).join(" and ")} items. ` +
    `${undrawable.map((kind) => `\`${kind}\``).join(", ")} ` +
    `${undrawable.length === 1 ? "is" : "are"} drawn by no surface today — ` +
    `return a kit's components as separate items rather than one bundle item.`
  );
}

/**
 * The behaviour disclosure, derived from
 * {@link AGENTIC_LOGIC_SURFACE_SUPPORTED_INTENTS} so it can never claim an
 * action the publish gate refuses.
 *
 * Sibling of {@link renderRenderableKindsLine}: that one says what a surface
 * can DRAW, this one says what it can DO. Both are rendered rather than
 * written out, because the model reads a projection with every zod
 * `.describe()` stripped.
 */
function renderSupportedIntentsLine(
  surfaces: readonly AgenticLogicSurface[]
): string {
  const targeted = surfaces.length > 0 ? surfaces : AgenticLogicSurfaceList;
  const supported = agenticLogicSupportedIntents(targeted);
  const unsupported = agenticLogicUnsupportedIntents(
    AgenticLogicItemIntentList,
    targeted
  );

  const opening =
    `Items you return must ask for an ACTION the target surface can perform: ` +
    `${list(targeted)} can only ` +
    `${supported.map((intent) => intent.toUpperCase()).join(" or ")} an item.`;

  if (unsupported.length === 0) return opening;

  return (
    `${opening} ` +
    `${unsupported.map((intent) => `\`intent: "${intent}"\``).join(", ")} ` +
    `${unsupported.length === 1 ? "is" : "are"} materialized by no surface ` +
    `today and ${unsupported.length === 1 ? "is" : "are"} REFUSED at publish ` +
    `— recommend the swap target with \`intent: "add"\` instead.`
  );
}

/**
 * Per-field availability notes, keyed by field — the same sentences
 * {@link renderAgenticLogicRequestContract} puts in its list, for consumers
 * that attach them to a field rather than render a block. The editor-facing
 * TypeScript uses this so a hover over `request.order` says where it can be
 * read, and the prose and the types cannot drift apart.
 */
export function renderAgenticLogicRequestFieldNotes(
  surfaces: readonly AgenticLogicSurface[] = AgenticLogicSurfaceList
): Record<AgenticLogicSurfaceInputField, string> {
  return Object.fromEntries(
    AgenticLogicSurfaceInputFieldList.map((field) => [
      field,
      availabilityNote(field, surfaces),
    ])
  ) as Record<AgenticLogicSurfaceInputField, string>;
}

/**
 * One line per surface-dependent field: what it is, and where it can actually
 * be read. `surfaces` narrows the answer to the surfaces a rule targets, so an
 * author writing a post-purchase rule is told "cart is NEVER sent" rather than
 * being handed the whole matrix to work out for themselves.
 */
export function renderAgenticLogicRequestContract(
  surfaces: readonly AgenticLogicSurface[] = AgenticLogicSurfaceList
): string {
  const scope =
    surfaces.length === AgenticLogicSurfaceList.length
      ? "Each surface sends different shopper context."
      : `This rule targets ${list(surfaces)}.`;

  const lines = AgenticLogicSurfaceInputFieldList.map((field) => {
    const description = AgenticLogicIngressRequestDescriptions[field];
    return `- \`request.${field}\` — ${description} ${availabilityNote(field, surfaces)}`;
  });

  return [
    `${scope} A field a surface never sends reads as null on every request, so a rule keyed on it there returns nothing and is recorded as an ordinary empty result — no error, nothing to debug.`,
    "",
    ...lines,
    "",
    // A2: the clock rides the sentence that already names it, because this
    // render is the Atlas chat's `requestContract` — the fact has to reach the
    // authoring turn, not only the types tab.
    `\`request.surface\`, \`request.placementId\`, \`request.shipping\` and \`request.evaluatedAt\` are sent on every surface. ${AGENTIC_LOGIC_EVALUATION_CLOCK_CONTRACT}`,
    "",
    // Stated here, one level up from `request.*`, because this is the only
    // rendering an authoring model and the editor both read. `candidates`
    // sits on the INVOCATION, so the per-field availability table above can
    // never reach it — and its "RESERVED, never populated in V1" sentence is
    // exactly what got stripped when the authoring types were copied out of
    // the zod contract.
    "`invocation.candidates` is RESERVED and is never populated in V1 on any surface. There is no upstream candidate feed and no shopper search term anywhere in the request — a rule cannot re-rank, preserve, or filter search results.",
    "",
    // Rendered from AGENTIC_LOGIC_SURFACE_RENDERABLE_KINDS rather than written
    // out, so the sentence an authoring model reads and the table the publish
    // gate enforces cannot drift. Do NOT hardcode this into a prompt file.
    renderRenderableKindsLine(surfaces),
    "",
    // The behaviour half of the same question, rendered from
    // AGENTIC_LOGIC_SURFACE_SUPPORTED_INTENTS for the same reason. Do NOT
    // hardcode this into a prompt file.
    renderSupportedIntentsLine(surfaces),
  ].join("\n");
}

/**
 * The canonical TIME-ROTATION idiom: a seeded shuffle for the per-shopper
 * order, plus an offset over `request.evaluatedAt` for the per-visit one.
 *
 * Pasted verbatim into the model-facing projection AND into the authoring
 * prompt, and asserted at both ends, for the same reason
 * `AGENTIC_LOGIC_INSTANCED_ATTEMPT_EXAMPLE` and
 * `AGENTIC_LOGIC_RAW_QUERY_EXAMPLE_OPERATION` are — the doc and the prompt
 * cannot drift apart silently.
 *
 * It lives in this package rather than beside those two in `@intelligems/ig-utils`
 * because the note text that interpolates it is authored here, and `ig-utils`
 * may import only TYPES from `ig-types`.
 *
 * The day grain is the DEFAULT, not the contract: the note states the formula,
 * so a finer or coarser bucket stays authorable by changing the divisor.
 */
export const AGENTIC_LOGIC_TIME_ROTATION_EXAMPLE =
  "const shuffled = sdk.seededShuffle(eligible); " +
  "const day = Math.floor(request.evaluatedAt / 86_400_000); " +
  "const offset = shuffled.length === 0 ? 0 : day % shuffled.length; " +
  "const rotated = [...shuffled.slice(offset), ...shuffled.slice(0, offset)];";

/**
 * THE org-exclusion enforcement boundary, in one sentence.
 *
 * Interpolated into the `sdk.getOrgExclusions` authoring note AND pasted
 * verbatim into both agentic-rule-creation prompts, asserted at both ends, for
 * the same reason {@link AGENTIC_LOGIC_TIME_ROTATION_EXAMPLE} is.
 *
 * T10: with this fact published nowhere in `web/ai`, the model generalised from
 * the dataset store's OPT-IN read contract — the one adjacent org-scoped store
 * it is told about — and told a merchant that custom code must enforce their
 * store exclusions itself. The truth is the opposite, and the false version is
 * the unsafe direction: it invites duplicated policy logic inside rule source.
 *
 * Every clause is load-bearing and traceable:
 * - "the platform applies" — `applyServingPolicy` is the broker's only serving
 *   exit (`agentic-logic-broker.service.ts:527,555` → `:735`);
 * - "every rule … whatever way it was authored" — nothing on that path reads
 *   `managedBy`;
 * - "fail-closed" — a throwing policy read removes every item as unverifiable
 *   (`agentic-logic-serve-exclusions.service.ts` `filter()`);
 * - "rule code cannot opt out of them or skip them" — there is no bypass to
 *   describe, so no merchant ever has to be told to re-implement one.
 */
export const AGENTIC_LOGIC_EXCLUSION_ENFORCEMENT_CONTRACT =
  "Org exclusions are applied BY THE PLATFORM on the serving path to every rule's output, " +
  "for every rule and whatever way it was authored — code, builder or template — and the filter is " +
  "fail-closed: if the org policy cannot be read, nothing is served. Rule code cannot opt out of them, " +
  "skip them, or be required to re-apply them.";

/**
 * WHO STAMPS THE EVALUATION CLOCK, and where an instant IS settable.
 *
 * Interpolated into the `request.evaluatedAt` authoring note, appended to the
 * rendered request contract, and pasted verbatim into the code-authoring
 * prompt, asserted at every end, for the same reason
 * {@link AGENTIC_LOGIC_EXCLUSION_ENFORCEMENT_CONTRACT} is.
 *
 * A2 (atlas-endorses-caller-set-timestamp): everything the authoring model
 * reads said `evaluatedAt` is PINNED and none of it said who pins it. "Pinned"
 * without a stamper reads as "whatever instant this request carries", so a
 * merchant's "we'll pass the timestamp in / replay yesterday's traffic" premise
 * was echoed back and shipped in a proposal.
 *
 * Every clause is load-bearing and traceable:
 * - "STAMPED BY THE PLATFORM … broker … publish gates" — the
 *   `AgenticLogicRequestSchema` docblock and the ingress schema that omits the
 *   field (`functions/agentic-logic/types/request.ts`);
 * - "dropped without an error" — `AgenticLogicIngressRequestSchema` strips an
 *   unknown `evaluatedAt` rather than refusing it, locked by
 *   `functions/agentic-logic/types/index.test.ts` ("a caller-supplied
 *   `evaluatedAt` is stripped"). Saying the strip out loud is what stops a
 *   caller reading silence as success;
 * - "TOP-LEVEL sibling of `request`" — `AgenticLogicValidationScenarioSchema`
 *   carries `evaluatedAt` beside `request`, not inside it
 *   (`functions/agentic-logic/gates/validation-scenario.ts`), which is exactly
 *   the nesting a wrong pin gets silently ignored at.
 */
export const AGENTIC_LOGIC_EVALUATION_CLOCK_CONTRACT =
  "`request.evaluatedAt` is STAMPED BY THE PLATFORM: the broker stamps it on every serving, preview, " +
  "API and evaluate ingress, and the publish gates stamp it once per job. A caller cannot set, " +
  "override or forge it — an `evaluatedAt` sent on an evaluate or serve body is not honoured, it is " +
  "dropped without an error. To judge a rule at a CHOSEN instant — replaying past traffic, testing a " +
  "boundary — pin `evaluatedAt` on a VALIDATION SCENARIO, where it is a TOP-LEVEL sibling of " +
  "`request` and never a key inside `request`.";

/**
 * Every SDK field an authoring model can write a value for or read one from,
 * as a flat namespaced key.
 *
 * The namespace prefixes are not decoration: `scan` is a key of BOTH the query
 * input and the returned page, and they mean different things — one is a bound
 * the author sets, the other is telemetry about what the read actually did.
 */
export type AgenticLogicSdkNoteKey =
  | `query.${keyof AgenticLogicProductQuery & string}`
  | `serverWhere.${keyof AgenticLogicProductServerWhere & string}`
  | `postFetch.${keyof AgenticLogicProductPostFetchWhere & string}`
  | `page.${keyof AgenticLogicProductPage & string}`
  | `context.${keyof AgenticLogicContext & string}`
  // The SHIPPING namespace. X8: `remainingToFreeShipping?: number | null;`
  // reached the model bare, and a rule froze the merchant's illustrative "$18"
  // into `!== 1800` plus a literal `price: { gte: 1800, lte: 2800 }` window —
  // a point function that serves nothing at $17.99. What the bare type cannot
  // state is that the quantity is CONTINUOUS, that the threshold is a setting
  // read per request rather than a constant, and that the state gates what the
  // copy may claim. Derived like the other branches, so a future shipping
  // field is a build error until someone says what the model is told about it.
  | `shipping.${keyof AgenticLogicShippingContext & string}`
  // The RESULT-ITEM namespace — what the model WRITES, where the other
  // field-bearing branches are what it reads. Its absence is what let
  // `intent`, `discount` and `sellingPlanId` reach the model as bare types
  // with their RESERVED status living only in a zod `.describe()` this
  // projection strips.
  | `resultItem.${keyof AgenticLogicResultItem & string}`
  // The two branches that are EXPLICITLY ENUMERATED rather than derived. Every
  // other branch keys off a field-bearing type; these two name the classes the
  // union cannot reach:
  //
  //   - an SDK METHOD ARGUMENT (`availability.ids`), which has no
  //     field-bearing type to derive from, so its hidden ceiling would sit
  //     outside the table; and
  //   - an SDK METHOD whose contract is not in its signature
  //     (`sdk.seededShuffle`). Its return value IS fully described by `T[]` —
  //     what the type cannot state is its SEED, and the seed is the thing that
  //     decides what the shuffle varies along. Seeded on the visitor and the
  //     active rule version, it varies between shoppers and is CONSTANT across
  //     one shopper's visits. X7 shipped a rule sold as "rotates" that rotates
  //     for nobody, because the bare signature reads as generic randomness.
  //
  //     `sdk.getOrgExclusions` is the same class from the other side: its
  //     RETURN SHAPE is fully described by `AgenticLogicOrgExclusions`, and the
  //     thing an author actually needs — WHO APPLIES the policy — is not in the
  //     signature at all. T10: with that unpublished, the model generalised
  //     from the dataset store's opt-in read and told a merchant custom code
  //     must enforce their store exclusions itself.
  //
  // Enumerating all three is what lets the `satisfies Record<…>` completeness
  // invariant cover a method's hidden semantics the same way it covers a
  // field's.
  | "availability.ids"
  | "sdk.seededShuffle"
  | "sdk.getOrgExclusions"
  //   - a REQUEST field whose PROVENANCE is not in its type
  //     (`request.evaluatedAt`). `number` states the value and nothing about
  //     who produced it, and the caller-facing schema that OMITS the field —
  //     the whole proof that a caller never supplies it — is not visible from
  //     the projection at all. A2: told only that the instant is "pinned", the
  //     model endorsed a merchant's plan to pass a timestamp in and replay
  //     historical traffic through an evaluate call.
  //
  //     It is enumerated rather than derived because the request namespace has
  //     exactly one such member: every other request field is rendered with
  //     its description by `renderAgenticLogicRequestContract`, and only this
  //     one reaches the model as a bare declaration in the projected types.
  | "request.evaluatedAt";

/**
 * Whether a field carries a note in the authoring projection, and — when it
 * does not — why not.
 */
export type AgenticLogicSdkFieldCoverage =
  | { readonly noted: true }
  | { readonly noted: false; readonly because: string };

/**
 * THE COMPLETENESS INVARIANT for the authoring projection.
 *
 * The projection the model reads is plain TypeScript with every zod
 * `.describe()` stripped, so a field whose meaning lives only in a description
 * reaches the model as a bare type. That has now shipped five broken rules —
 * `scan.maxProducts: 10000` against a ceiling of 1000, `<= 50.00` for "under
 * $50", a `limit` the model had to guess at, a rule built entirely on the
 * reserved `candidates`, and a checkout rule that authored `intent: "replace"`
 * and had the swap target rendered as an ADD beside the line it was meant to
 * replace — and each was fixed by adding one note, which is why it keeps
 * recurring.
 *
 * The fifth got through because this invariant did not SPAN the result item:
 * the key type covered `query.*`, `serverWhere.*`, `postFetch.*`, `page.*`,
 * `context.*` and the accessor, i.e. everything the model READS and nothing it
 * WRITES. Three RESERVED fields — `intent`, `discount`, `sellingPlanId` — sat
 * outside the table entirely, so nothing obliged anyone to state what the
 * model is told about them. `resultItem.*` is now a branch of the key type for
 * exactly that reason.
 *
 * `satisfies Record<AgenticLogicSdkNoteKey, …>` closes the class: adding a
 * field to the query, the where clauses, the page, the context or the RESULT
 * ITEM is a BUILD ERROR until someone states what the model should be told
 * about it, or writes down why nothing needs saying. `noted: true` then obliges
 * {@link renderAgenticLogicSdkFieldNotes} to return text for that key, because
 * its return type is derived from this table.
 *
 * The opt-outs are deliberately not a blanket "self-evident": each says what
 * makes the bare type sufficient, so a reviewer can disagree with a specific
 * claim rather than with a silence.
 */
export const AGENTIC_LOGIC_SDK_FIELD_NOTE_COVERAGE = {
  "query.ids": {
    noted: false,
    because:
      "A GID array whose meaning the type states. The GID convention is stated once at the top of the projection.",
  },
  "query.collectionId": {
    noted: false,
    because:
      "The projection's two query forms are separate types, so which predicates are legal with a collection is a type error rather than a note.",
  },
  "query.where": {
    noted: false,
    because:
      "A container. Its two halves carry the meaning, and the server/postFetch split is documented on the projected types themselves.",
  },
  "query.orderBy": {
    noted: false,
    because:
      "The field union and direction are fully expressed by the projected type.",
  },
  "query.limit": { noted: true },
  "query.scan": { noted: true },
  "query.include": {
    noted: false,
    because:
      "An explicit metafield-ref array; the ref type states namespace and key.",
  },
  "serverWhere.price": { noted: true },
  "serverWhere.tags": {
    noted: false,
    because:
      "The projection already comments the anyOf/allOf collection caveat where it differs between the two query forms.",
  },
  "serverWhere.productType": {
    noted: false,
    because: "A plain any-of over a string field.",
  },
  "serverWhere.vendor": {
    noted: false,
    because: "A plain any-of over a string field.",
  },
  "serverWhere.title": {
    noted: false,
    because:
      "The projection comments the prefix-only restriction and the collection exclusion at the type.",
  },
  "serverWhere.productFilters": {
    noted: false,
    because:
      "The projection comments that these are valid only with a collectionId, and each filter member is its own named type.",
  },
  "postFetch.title": {
    noted: false,
    because: "A substring match; the type says `contains`.",
  },
  "postFetch.tags": {
    noted: false,
    because:
      "The projection comments that this is the only way to require several tags inside a collection.",
  },
  "postFetch.metafields": {
    noted: false,
    because:
      "The predicate members (equals/in/exists) are spelled out in the projected type.",
  },
  "postFetch.optionValues": {
    noted: false,
    because: "A name plus an any-of over its values; the type states both.",
  },
  "page.items": {
    noted: false,
    because: "The products, in the order the read produced them.",
  },
  "page.pageInfo": {
    noted: false,
    because: "Cursor facts whose two fields are self-describing.",
  },
  "page.scan": { noted: true },
  "context.slotsNeeded": { noted: true },
  "availability.ids": { noted: true },
  "sdk.seededShuffle": { noted: true },
  "sdk.getOrgExclusions": { noted: true },
  "request.evaluatedAt": { noted: true },
  "context.isPreview": {
    noted: false,
    because:
      "A boolean a rule may branch on but is never obliged to; nothing about it is hidden by the projection.",
  },
  // The shipping context — the block whose numbers a merchant says out loud,
  // which is exactly what makes them look like constants. Every field whose
  // meaning is CONTINUITY, ROT or CLAIM POLICY is noted; the one opt-out says
  // what makes the bare type enough.
  "shipping.state": { noted: true },
  "shipping.currency": {
    noted: false,
    because:
      "An ISO-4217 code the projected type and the module-level minor-units convention already state.",
  },
  "shipping.freeShippingThreshold": { noted: true },
  "shipping.remainingToFreeShipping": { noted: true },
  "shipping.tiers": { noted: true },
  "shipping.delivery": { noted: true },
  // The result item — what the model WRITES. Every field whose meaning is
  // currently description-only is noted; the opt-outs each say what makes the
  // bare type enough.
  "resultItem.kind": {
    noted: false,
    because:
      "A closed union the projected type spells out, and which kinds a surface can draw is stated once by the renderability line in the rendered request contract.",
  },
  "resultItem.id": {
    noted: false,
    because:
      "A GID whose scoping by `kind` is stated in the projected item type, and the GID convention is stated once at the top of the projection.",
  },
  "resultItem.defaultVariantId": {
    noted: false,
    because:
      "A variant GID whose name states its job — the pre-selected variant. Which variants may be offered is `allowedVariantIds`' note, not this one's.",
  },
  "resultItem.allowedVariantIds": { noted: true },
  "resultItem.sellingPlanId": { noted: true },
  "resultItem.intent": { noted: true },
  "resultItem.discount": { noted: true },
  "resultItem.sourceBranch": {
    noted: false,
    because:
      "A free-text telemetry label with no ceiling, no reservation and no surface behaviour attached — the bare `string` is the whole contract.",
  },
  "resultItem.children": {
    noted: false,
    because:
      "Bundle-only, and `bundle` is drawn by no surface today — the renderability line already refuses the parent item, so a note on its children would teach a shape that cannot ship.",
  },
} as const satisfies Record<
  AgenticLogicSdkNoteKey,
  AgenticLogicSdkFieldCoverage
>;

/** The keys {@link renderAgenticLogicSdkFieldNotes} must return text for. */
export type AgenticLogicSdkNotedField = {
  [
    K in keyof typeof AGENTIC_LOGIC_SDK_FIELD_NOTE_COVERAGE
  ]: (typeof AGENTIC_LOGIC_SDK_FIELD_NOTE_COVERAGE)[K]["noted"] extends true
    ? K
    : never;
}[keyof typeof AGENTIC_LOGIC_SDK_FIELD_NOTE_COVERAGE];

/**
 * The SDK fields the authoring TypeScript cannot state for itself, keyed by the
 * namespaced field name.
 *
 * Same seam and same reason as {@link renderAgenticLogicRequestFieldNotes}: the
 * model reads a PROJECTION of the SDK types built in `@intelligems/ig-utils`,
 * which may import only types from this package, so the zod `.describe()` text
 * and every platform constant are stripped out of what it reads. S7's rule
 * asked for `scan.maxProducts: 10000` — ten times a ceiling the model was
 * never told — and the schema refused the read before it happened.
 *
 * The numbers are interpolated from the constants the schema enforces, so
 * moving a ceiling moves this text. WHICH fields must appear here is not a
 * judgement call: it is derived from
 * {@link AGENTIC_LOGIC_SDK_FIELD_NOTE_COVERAGE}.
 */
export function renderAgenticLogicSdkFieldNotes(): Record<
  AgenticLogicSdkNotedField,
  string
> {
  return {
    "query.limit": [
      AgenticLogicProductQueryDescriptions.limit,
      `An integer from 1 to ${AGENTIC_LOGIC_PRODUCT_QUERY_MAX_LIMIT}. A value outside that range is REFUSED as a validation error before the read — it is not clamped down to the ceiling, so a rule that asks for more than ${AGENTIC_LOGIC_PRODUCT_QUERY_MAX_LIMIT} returns nothing at all.`,
      `Ask for MORE than you render whenever anything is filtered after the read (cart exclusions, tag exclusions, availability): a read sized at exactly \`context.slotsNeeded\` turns every discarded candidate into a slot the placement never fills, and a \`.filter()\` in rule code cannot backfill. \`context.slotsNeeded * ${AGENTIC_LOGIC_QUERY_LIMIT_HEADROOM_FACTOR} + ${AGENTIC_LOGIC_QUERY_LIMIT_HEADROOM_FLOOR}\` is the widest the publish gate allows, and it warns from both sides. A predicate the SDK can evaluate belongs in \`where\` instead — the post-fetch scan pages until it has \`limit\` MATCHES, so those do backfill. To top up from a second source, use \`fillTo(context.slotsNeeded, [...])\`.`,
    ].join(" "),
    "query.scan": [
      AgenticLogicProductScanDescriptions._schema,
      `\`maxProducts\` may only LOWER the platform bound of ${AGENTIC_LOGIC_PRODUCT_SCAN_MAX_PRODUCTS} products; a larger value is refused before the first read. Omit it to take ${AGENTIC_LOGIC_PRODUCT_SCAN_MAX_PRODUCTS}.`,
      `\`pageSize\` is at most ${AGENTIC_LOGIC_PRODUCT_SCAN_MAX_PAGE_SIZE}, which is also the default.`,
      "Narrow with server filters first: a scan the server could have done is products fetched for nothing.",
    ].join(" "),
    "serverWhere.price": [
      AgenticLogicPriceFilterDescriptions._schema,
      `\`gte\`/\`lte\` are INCLUSIVE and \`gt\`/\`lt\` are EXCLUSIVE — "under $50" is \`lt\`, "$50 or less" is \`lte\`, and choosing the wrong one silently shifts the boundary by one product.`,
      "Every threshold is an integer in MINOR UNITS of the shop currency, so $50.00 is 5000, never 50 and never 50.00.",
    ].join(" "),
    "page.scan": [
      AgenticLogicScanTelemetryDescriptions._schema,
      AgenticLogicScanTelemetryDescriptions.truncated,
      "A rule that sorts `items` from a truncated scan ranks a prefix, not the catalogue — read this flag and either narrow the read or say what the ranking is over.",
    ].join(" "),
    "availability.ids": [
      `Refs are counted per VARIANT REF, not per product: \`products.flatMap((p) => p.variants…)\` over a shortlist of N products costs Σ variants, which on a size × colour catalogue is tens of refs per product.`,
      `The SDK PAGES this list at ${AGENTIC_LOGIC_STOREFRONT_INPUT_LIST_MAX} refs per read, so a long list is never refused — but EACH PAGE SPENDS ONE of the ${AGENTIC_LOGIC_MAX_SDK_READS_PER_INVOCATION} SDK reads an invocation gets, and running out kills the invocation.`,
      "So bound the ref list by what you will actually render, not by a product count.",
    ].join(" "),
    // X7: the bare `seededShuffle<T>(items) => T[]` reads as generic
    // randomness. The seed — the only thing that decides what the shuffle
    // varies ALONG — is `${visitorId}:${ruleVersion}` and nothing else, and it
    // appears nowhere in the type. Atlas published a shuffle-only rule and told
    // the merchant it rotates.
    "sdk.seededShuffle": [
      "Seeded from the VISITOR ID and the ACTIVE RULE VERSION, and nothing else. Rule code can neither see nor choose the seed.",
      "So it varies the order BETWEEN SHOPPERS: two shoppers get different orders, and THE SAME SHOPPER GETS THE SAME ORDER ON EVERY VISIT until the rule is republished. A shuffle alone is not a rotation.",
      `To rotate over time as well, add a time term over \`request.evaluatedAt\`: ${AGENTIC_LOGIC_TIME_ROTATION_EXAMPLE}`,
      "That is safe by construction: `request.evaluatedAt` is pinned once per invocation and reused across the two runs the stable-output gate compares, so branching on it stays deterministic. `86_400_000` is one day — change the divisor for a different grain. NEVER `Date.now()`: the source guard refuses it.",
      'Copy lock: "varies the order" is true only ACROSS SHOPPERS. Copy that promises a returning shopper something different requires the time term in the source.',
    ].join(" "),
    // T10 (atlas-says-code-rules-must-enforce-org-exclusions): the bare
    // `getOrgExclusions(): Promise<AgenticLogicOrgExclusions>` describes the
    // SHAPE and says nothing about WHO APPLIES IT. With that unpublished, the
    // model generalised from the dataset store — the one adjacent org-scoped
    // store it IS told about, whose contract is an opt-in read — and told a
    // merchant that custom code must enforce their store exclusions.
    "sdk.getOrgExclusions": [
      AGENTIC_LOGIC_EXCLUSION_ENFORCEMENT_CONTRACT,
      "So this read is for AWARENESS, never for enforcement: use it to avoid spending slots and reads on candidates the platform is about to strip, or to top up from another source when a ban would empty the envelope. A rule that never calls it is exactly as guarded as one that does.",
      "Pinned per invocation — an edit the merchant makes is effective on the NEXT invocation, so it is never a live read within one evaluation.",
      "Copy lock: NEVER tell a merchant that rule code must read or enforce their exclusions, and never present enforcement as something a hand-written rule takes on. Datasets are an opt-in read the rule performs; exclusions are a platform filter the rule cannot opt out of, opt into, or observe the effect of.",
    ].join(" "),
    // A2 (atlas-endorses-caller-set-timestamp): the declaration is
    // `evaluatedAt: number;`, and `number` cannot say who produced it. The doc
    // comment it replaces said "pinned for the whole invocation" and never
    // named a stamper, so the model read the instant as the caller's to supply.
    "request.evaluatedAt": [
      AgenticLogicRequestDescriptions.evaluatedAt,
      AGENTIC_LOGIC_EVALUATION_CLOCK_CONTRACT,
      "Dates elsewhere in the request are ISO-8601 strings, so compare by parsing: `(request.evaluatedAt - Date.parse(order.createdAt)) / 86_400_000` is the number of days since the order.",
    ].join(" "),
    "context.slotsNeeded": [
      AgenticLogicContextDescriptions.slotsNeeded,
      "ZERO IS REACHABLE: a placement can resolve to no slots, and the invocation is delivered rather than refused. Guard on `context.slotsNeeded <= 0` and return an empty envelope BEFORE any catalog read — reading first spends a read the surface will discard, and SDK query limits are positive so the read itself would fail.",
    ].join(" "),
    // X8 (rule-hardcodes-exact-shipping-gap): the merchant said "$18 from free
    // shipping" and the rule shipped `remainingToFreeShipping !== 1800` plus a
    // `price: { gte: 1800, lte: 2800 }` window. Every gate passed — one
    // non-empty envelope is all `produces_output` asks — and the rule serves
    // nothing at $17.99. What the bare `number | null` cannot say is that the
    // quantity is CONTINUOUS and that the merchant's number is an EXAMPLE.
    "shipping.remainingToFreeShipping": [
      AgenticLogicShippingContextDescriptions.remainingToFreeShipping,
      "A CONTINUOUS quantity: it moves with every cart edit and every change to the merchant's shipping settings. COMPARE it — `gap > 0 && gap <= ceiling` — and never test it for equality. `=== N` or `!== N` makes the rule a point function that serves nothing one minor unit either side of `N`.",
      'A number the merchant says out loud ("$18 from free shipping") is an EXAMPLE OF A CLASS, not a constant. Derive the candidate window FROM the field — `price: { gte: gap, lte: gap + headroom }` — and never restate the merchant\'s example as a literal bound.',
      "`null` or absent means UNRESOLVABLE, not zero. Branch on it; `0` means the visitor ALREADY qualifies, which is a different rule.",
      "Integer MINOR UNITS of `shipping.currency`, as everywhere else: $18.00 is 1800.",
    ].join(" "),
    "shipping.freeShippingThreshold": [
      AgenticLogicShippingContextDescriptions.freeShippingThreshold,
      "The merchant's CURRENT setting, read fresh on every request. Copying its value into rule source is precisely the rot this field exists to prevent: the day the merchant moves the threshold, a hardcoded number keeps the rule reasoning about a threshold nobody set.",
      "Absent means there is NO free-shipping incentive — branch on that rather than assuming one.",
      "Integer MINOR UNITS of `shipping.currency`.",
    ].join(" "),
    "shipping.state": [
      AgenticLogicShippingContextDescriptions.state,
      "Under `assumed` the numbers are best-effort defaults that may be wrong for this visitor, and under `not-applicable` there is no shipping incentive to read at all — branch on the state rather than trusting the thresholds blindly.",
    ].join(" "),
    "shipping.tiers": [
      AgenticLogicShippingContextDescriptions.tiers,
      'ASCENDING by `threshold`, and the rungs BELOW the top are merely CHEAPER shipping, not free shipping — reading `tiers[0]` as "the free one" is the same class of bug as freezing a gap into a literal. Select the rung by comparing its `threshold` against the cart.',
      "Each rung's `label` is the merchant's own wording, for the rule's reading only — a rule emits no shopper-visible text.",
      "Thresholds are integer MINOR UNITS of `shipping.currency`.",
    ].join(" "),
    "shipping.delivery": [
      AgenticLogicShippingContextDescriptions.delivery,
      "OBSERVED FACTS from the live checkout, not merchant settings: the options Shopify computed, which one is `selected`, each option's after-discount `cost` in integer MINOR UNITS of its `currency`, its `carrierName` and `transitDays` when Shopify reports them, and `isLocalPickup` when every selected option is a pickup. Branch on `request.shipping.delivery?.selected` for the buyer's choice, or scan `options` when several groups select differently.",
      "It is absent whenever nothing has been computed yet, so guard with optional chaining and give the no-delivery branch a sensible default — it is the branch every checkout renders first.",
    ].join(" "),
    "resultItem.allowedVariantIds": [
      AgenticLogicResultItemDescriptions.allowedVariantIds,
      "ABSENT means unrestricted — every variant is allowed. A present list is EXHAUSTIVE, and an EMPTY one allows nothing, which drops the item rather than rendering something unaddable.",
    ].join(" "),
    "resultItem.sellingPlanId": [
      AgenticLogicResultItemDescriptions.sellingPlanId,
      "RESERVED at envelope version 1: no surface attaches a selling plan, so setting it subscribes nothing. Recommend the one-time purchase instead.",
    ].join(" "),
    // Rendered from the capability table rather than written out, so the
    // sentence the model reads and the check the publish gate runs cannot
    // drift — the day a surface gains a cart transform, this text follows it.
    "resultItem.intent": [
      AgenticLogicResultItemDescriptions.intent,
      `Supported on every surface today: ${agenticLogicSupportedIntents([])
        .map((intent) => `\`"${intent}"\``)
        .join(", ")}.`,
      `${agenticLogicUnsupportedIntents(AgenticLogicItemIntentList, [])
        .map((intent) => `\`"${intent}"\``)
        .join(", ")} is materialized by NO surface and is REFUSED at publish.`,
    ].join(" "),
    "resultItem.discount": [
      AgenticLogicResultItemDescriptions.discount,
      "RESERVED at envelope version 1: no surface reads this, and emitting it is REFUSED by the envelope check. A rule never prices what it recommends — the discount and the struck-through price are set by the merchant on the placement the rule is attached to.",
    ].join(" "),
  };
}
